/**
 * useDeploymentPolling
 *
 * Custom hook that polls GET /api/deployments?deploymentId={id} for all
 * active (non-terminal) deployments every 15 seconds.
 *
 * Features:
 * - 15s fixed polling interval
 * - 7-minute max duration per deployment, then marks "timed out"
 * - Updates Redux slices for both dashboard and tenant views
 * - Persists to localStorage via deploymentTrackingSlice
 * - Resumes polling on mount if non-terminal items exist
 * - Supports multiple concurrent deployments
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  selectTrackedDeployments,
  updateTrackedStatus,
  markTimedOut,
  loadFromStorage,
} from '../slices/deploymentTrackingSlice';
import { updateTenantDeploymentStatuses } from '../slices/dashboardPoliciesSlice';
import { updatePolicyDeploymentStatuses } from '../slices/tenantPoliciesSlice';
import { applyDeploymentStatus as applyLabelStatus, LabelUIStatus } from '../slices/labelDeploymentSlice';
import { getDeploymentStatus } from '../services/deployment.service';
import {
  TERMINAL_DEPLOYMENT_STATUSES,
  DEPLOYMENT_POLL_INTERVAL_MS,
  DEPLOYMENT_POLL_MAX_DURATION_MS,
  mapApiStatusToDeploymentStatus,
  TrackedDeployment,
} from '../services/types';

/**
 * App-level hook that polls deployment status for all active tracked items.
 * Must be mounted in a component that is ALWAYS rendered (e.g. App.tsx)
 * so polling persists across tab/view switches and page refreshes.
 */
export function useDeploymentPolling(): void {
  const dispatch = useAppDispatch();
  const tracked = useAppSelector(selectTrackedDeployments);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep a ref to the latest tracked items so the interval callback never
  // captures a stale closure.
  const trackedRef = useRef(tracked);
  trackedRef.current = tracked;

  // Build the status key used by dashboard/tenant slices: "entityId::tenantId"
  const statusKey = (entityId: string, tenantId: string) =>
    `${entityId.toLowerCase()}::${tenantId.toLowerCase()}`;

  // Poll a single deployment (stable — only depends on dispatch)
  const pollOne = useCallback(
    async (item: TrackedDeployment) => {
      // Check 7-minute timeout
      if (Date.now() - item.startedAt > DEPLOYMENT_POLL_MAX_DURATION_MS) {
        dispatch(markTimedOut(item.deploymentId));
        return;
      }

      try {
        const response = await getDeploymentStatus(item.deploymentId, item.tenantId);
        const newApiStatus = response.deploymentStatus || item.apiStatus;

        // Update tracking slice
        dispatch(
          updateTrackedStatus({
            deploymentId: item.deploymentId,
            apiStatus: newApiStatus,
            errorMessage: response.error?.errorMessage,
            warnings: response.warnings,
          }),
        );

        // Map to UI deployment status and update the appropriate slice
        const uiStatus = mapApiStatusToDeploymentStatus(newApiStatus);
        const key = statusKey(item.entityId, item.tenantId);

        if (item.entityType === 'LABEL') {
          const labelStatus: LabelUIStatus =
            uiStatus === 'Deployed' ? 'Deployed'
            : uiStatus === 'DeploymentFailed' ? 'DeploymentFailed'
            : uiStatus === 'NotDeployed' ? 'NotDeployed'
            : 'InProgress';
          dispatch(
            applyLabelStatus({
              entityId: item.entityId,
              tenantId: item.tenantId,
              status: labelStatus,
            }),
          );
        } else if (item.view === 'dashboard') {
          dispatch(updateTenantDeploymentStatuses([[key, uiStatus]]));
        } else {
          dispatch(updatePolicyDeploymentStatuses([[key, uiStatus]]));
        }
      } catch (err) {
        // Don't remove on transient errors — just log and retry next interval
        console.warn(
          `[DeploymentPolling] Failed to poll ${item.deploymentId}:`,
          err instanceof Error ? err.message : err,
        );
      }
    },
    [dispatch],
  );

  // Poll all active deployments — reads from ref so it's always fresh
  const pollAll = useCallback(() => {
    const items = Object.values(trackedRef.current);
    if (items.length === 0) return;

    items.forEach(item => {
      if (TERMINAL_DEPLOYMENT_STATUSES.has(item.apiStatus)) return;
      pollOne(item);
    });
  }, [pollOne]);

  // Load from localStorage on mount
  useEffect(() => {
    dispatch(loadFromStorage());
  }, [dispatch]);

  // Start/stop polling interval based on tracked items
  useEffect(() => {
    const count = Object.keys(tracked).length;

    if (count > 0) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Immediate poll on mount / when new items appear
      pollAll();

      // Set up interval — pollAll reads from trackedRef so it's never stale
      intervalRef.current = setInterval(pollAll, DEPLOYMENT_POLL_INTERVAL_MS);
    } else {
      // No active items — clear interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [Object.keys(tracked).length, pollAll]);
}
